import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    EVENT_TYPE_OPTIONS,
    BUDGET_OPTIONS,
    GOAL_OPTIONS,
    PULSE_TESTIDS,
} from "@/lib/pulseConstants";
import { submitLead } from "@/lib/api";
import { trackFormStartOnce, trackGenerateLead } from "@/lib/analytics";

const INITIAL = {
    name: "",
    company: "",
    email: "",
    phone: "",
    brand: "",
    event_type: "",
    event_date: "",
    budget: "",
    goal: "",
    message: "",
    website: "", // honeypot — must stay empty
};

export default function PulseBriefForm({ compact = false, sourcePage = "home" }) {
    const [values, setValues] = useState(INITIAL);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitError, setSubmitError] = useState("");

    function updateField(name, value) {
        // GA4 form_start — fires only once per session across the pulse brief
        // form. Not called for the hidden honeypot field.
        if (name !== "website") {
            trackFormStartOnce({ form_name: "pulse_brief", source_page: sourcePage });
        }
        setValues((v) => ({ ...v, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    }

    function validate() {
        const next = {};
        if (!values.name.trim()) next.name = "Your name is required.";
        if (!values.company.trim()) next.company = "Which company are you with?";
        if (!values.email.trim()) next.email = "Work email required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = "Enter a valid email.";
        if (!values.phone.trim()) next.phone = "Phone / WhatsApp required.";
        if (!values.brand.trim()) next.brand = "Brand / product please.";
        if (!values.event_type) next.event_type = "Pick an event type.";
        if (!values.budget) next.budget = "Pick a budget range.";
        if (!values.goal) next.goal = "Pick a primary goal.";
        if (!values.message.trim() || values.message.trim().length < 5) next.message = "Tell us at least a few words.";
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    async function onSubmit(e) {
        e.preventDefault();
        setSubmitError("");
        if (!validate()) {
            toast.error("Please complete the required fields.");
            return;
        }
        try {
            setSubmitting(true);
            await submitLead({ ...values, source_page: sourcePage });
            // GA4 generate_lead — fires only on confirmed backend success.
            // No PII: only categorical event_type + budget range + source page.
            trackGenerateLead({
                event_type: values.event_type,
                budget_range: values.budget,
                source_page: sourcePage,
                form_name: "pulse_brief",
            });
            setSuccess(true);
            toast.success("Your brief has landed — the Pulse team will be in touch.");
            setValues(INITIAL);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            let message;
            if (status === 429) {
                message = typeof detail === "string" ? detail : "Too many submissions from this connection. Please wait a minute and try again.";
            } else if (status === 422) {
                message = "Some fields need attention. Please check and try again.";
            } else if (typeof detail === "string") {
                message = detail;
            } else {
                message = "We couldn’t submit your brief. Please try again in a moment or email us directly.";
            }
            setSubmitError(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    }

    if (success) {
        return (
            <motion.div
                data-testid={PULSE_TESTIDS.form.success}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="rounded-2xl border border-white/10 bg-[#0B0C10] p-8 md:p-10 text-center"
            >
                <div className="mx-auto h-14 w-14 rounded-full bg-[#FF2A2A]/15 border border-[#FF2A2A]/40 flex items-center justify-center mb-6">
                    <CheckCircle2 className="text-[#FF2A2A]" size={28} />
                </div>
                <h3 className="display-3 mb-3">Your brief has landed.</h3>
                <p className="body-lg max-w-xl mx-auto">
                    The Pulse team will review your brief and get back to you soon.
                    Meanwhile, keep an eye on your inbox — we usually respond within 1–2 working days.
                </p>
                <div className="mt-6">
                    <Button
                        onClick={() => setSuccess(false)}
                        variant="outline"
                        className="rounded-full border-white/20 hover:border-white/40 bg-transparent text-white hover:bg-white/5"
                        data-testid="pulse-brief-submit-another"
                    >
                        Submit another brief
                    </Button>
                </div>
            </motion.div>
        );
    }

    return (
        <form
            data-testid={PULSE_TESTIDS.form.form}
            onSubmit={onSubmit}
            noValidate
            className="rounded-2xl border border-white/10 bg-[#0B0C10]/95 backdrop-blur p-6 md:p-8 relative overflow-hidden"
        >
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C46A2B]/15 blur-3xl pointer-events-none" aria-hidden="true" />

            {/* Honeypot — hidden from real users, visible to bots */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                    opacity: 0,
                    pointerEvents: "none",
                }}
            >
                <label htmlFor="pulse-website-field">Website (leave blank)</label>
                <input
                    id="pulse-website-field"
                    data-testid={PULSE_TESTIDS.form.honeypot}
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={values.website}
                    onChange={(e) => updateField("website", e.target.value)}
                />
            </div>

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name" required error={errors.name}>
                    <Input
                        data-testid={PULSE_TESTIDS.form.name}
                        value={values.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        placeholder="Your full name"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>
                <Field label="Company" required error={errors.company}>
                    <Input
                        data-testid={PULSE_TESTIDS.form.company}
                        value={values.company}
                        onChange={(e) => updateField("company", e.target.value)}
                        placeholder="Company / Agency"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>
                <Field label="Email" required error={errors.email}>
                    <Input
                        type="email"
                        data-testid={PULSE_TESTIDS.form.email}
                        value={values.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        placeholder="work@brand.com"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>
                <Field label="Phone / WhatsApp" required error={errors.phone}>
                    <Input
                        data-testid={PULSE_TESTIDS.form.phone}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={values.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        placeholder="+60 12 000 0000"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>
                <Field label="Brand / Product" required error={errors.brand} className="md:col-span-2">
                    <Input
                        data-testid={PULSE_TESTIDS.form.brand}
                        value={values.brand}
                        onChange={(e) => updateField("brand", e.target.value)}
                        placeholder="Which brand or product is this for?"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>

                <Field label="Type of Event" required error={errors.event_type}>
                    <Select value={values.event_type} onValueChange={(v) => updateField("event_type", v)}>
                        <SelectTrigger
                            data-testid={PULSE_TESTIDS.form.eventType}
                            className="bg-black/40 border-white/15 text-white focus:ring-[#FF2A2A] focus-visible:ring-[#FF2A2A]"
                        >
                            <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                            {EVENT_TYPE_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field label="Estimated Event Date" hint="Approximate is fine">
                    <Input
                        type="text"
                        data-testid={PULSE_TESTIDS.form.eventDate}
                        value={values.event_date}
                        onChange={(e) => updateField("event_date", e.target.value)}
                        placeholder="e.g. Q1 2026 or 15 March 2026"
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>

                <Field label="Estimated Budget" required error={errors.budget}>
                    <Select value={values.budget} onValueChange={(v) => updateField("budget", v)}>
                        <SelectTrigger
                            data-testid={PULSE_TESTIDS.form.budget}
                            className="bg-black/40 border-white/15 text-white focus:ring-[#FF2A2A] focus-visible:ring-[#FF2A2A]"
                        >
                            <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                            {BUDGET_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field label="What are you trying to achieve?" required error={errors.goal}>
                    <Select value={values.goal} onValueChange={(v) => updateField("goal", v)}>
                        <SelectTrigger
                            data-testid={PULSE_TESTIDS.form.goal}
                            className="bg-black/40 border-white/15 text-white focus:ring-[#FF2A2A] focus-visible:ring-[#FF2A2A]"
                        >
                            <SelectValue placeholder="Primary goal" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0B0C10] border-white/10 text-white">
                            {GOAL_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>

                <Field label="Tell us the moment you want to create" required error={errors.message} className="md:col-span-2" hint="Product story, campaign hook, audience, any concept fragments you already have">
                    <Textarea
                        data-testid={PULSE_TESTIDS.form.message}
                        rows={compact ? 4 : 6}
                        value={values.message}
                        onChange={(e) => updateField("message", e.target.value)}
                        placeholder="Give us the shape of the brief. We'll do the heavy lifting from there."
                        className="bg-black/40 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#FF2A2A]"
                    />
                </Field>
            </div>

            {submitError && (
                <div
                    data-testid="pulse-brief-error-message"
                    className="mt-6 rounded-xl border border-[#FF2A2A]/40 bg-[#FF2A2A]/10 px-4 py-3 text-sm text-[#FFB0B0]"
                >
                    {submitError}
                </div>
            )}

            <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs font-mono text-white/50 uppercase tracking-widest">
                    No spam. Only real briefs get replies.
                </p>
                <Button
                    type="submit"
                    disabled={submitting}
                    data-testid={PULSE_TESTIDS.form.submit}
                    className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 px-7 inline-flex items-center gap-2"
                >
                    {submitting ? (
                        <><Loader2 size={16} className="animate-spin" /> Sending your brief...</>
                    ) : (
                        <>Submit My Pulse Brief <Send size={16} /></>
                    )}
                </Button>
            </div>
        </form>
    );
}

function Field({ label, hint, required, error, children, className = "" }) {
    return (
        <div className={className}>
            <div className="flex items-baseline justify-between mb-1.5">
                <Label className="text-xs uppercase tracking-[0.14em] text-white/70 font-mono">
                    {label} {required && <span className="text-[#FF2A2A]">*</span>}
                </Label>
                {hint && <span className="text-[10px] text-white/40">{hint}</span>}
            </div>
            {children}
            {error && <p className="mt-1 text-xs text-[#FF6B6B]">{error}</p>}
        </div>
    );
}
